import { createTheme } from "@mui/material/styles";

const auditTheme = createTheme({
    spacing: 7,

    palette: {
        background: {
            default: "#f5f6fa",
            paper: "#ffffff",
        },
        primary: {
            main: "#2563eb",
            light: "#eff6ff",
            dark: "#1d4ed8",
        },
        secondary: {
            main: "#7c3aed",
        },
        grey: {
            50: "#f8fafc",
            100: "#f1f5f9",
            200: "#e2e8f0",
            300: "#cbd5e1",
            400: "#94a3b8",
            500: "#64748b",
            600: "#475569",
            700: "#334155",
            800: "#1e293b",
            900: "#0f172a",
        },
    },

    shape: { borderRadius: 8 },


    typography: {
        fontSize: 18,
        fontFamily: "'Inter', 'Roboto', sans-serif",
        body1: { fontSize: "1.0625rem", lineHeight: 1.5 },
        body2: { fontSize: "1rem", lineHeight: 1.5 },
        caption: { fontSize: "0.875rem", color: "#64748b" },
        overline: {
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: "#64748b",
            textTransform: "uppercase",
        },
        h5: { fontSize: "1.5rem", fontWeight: 700, color: "#0f172a" },
        h6: { fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" },
        subtitle1: { fontSize: "1.0625rem", fontWeight: 600 },
        subtitle2: { fontSize: "1rem", fontWeight: 600 },
    },

    components: {
        // ── Botones ───────────────────────────────────────────────
        MuiButton: {
            defaultProps: { size: "small", disableElevation: true },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 7,
                    fontSize: "0.8125rem",
                },
                contained: {
                    boxShadow: "none",
                    "&:hover": { boxShadow: "0 2px 8px rgba(37,99,235,0.25)" },
                },
                outlined: {
                    borderColor: "#e2e8f0",
                    color: "#334155",
                    "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
                },
                sizeSmall: { padding: "4px 12px" },
            },
        },
        MuiIconButton: {
            defaultProps: { size: "small" },
            styleOverrides: {
                root: {
                    borderRadius: 7,
                    color: "#475569",
                    "&:hover": { backgroundColor: "#f1f5f9" },
                },
                sizeSmall: { padding: 5 },
            },
        },

        // ── Inputs ────────────────────────────────────────────────
        MuiTextField: { defaultProps: { size: "small" } },
        MuiSelect: { defaultProps: { size: "small" } },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 7,
                    backgroundColor: "#fff",
                    fontSize: "0.8125rem",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2563eb",
                        borderWidth: 1.5,
                    },
                },
                input: { padding: "7px 11px" },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { fontSize: "0.8125rem" },
                sizeSmall: { fontSize: "0.8125rem" },
            },
        },

        // ── Cards ─────────────────────────────────────────────────
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
                    border: "1px solid #e2e8f0",
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: "12px 16px",
                    "&:last-child": { paddingBottom: 12 },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { borderRadius: 10 },
                outlined: { borderColor: "#e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
            },
        },

        // ── Chips ─────────────────────────────────────────────────
        MuiChip: {
            defaultProps: { size: "small" },
            styleOverrides: {
                root: { borderRadius: 5, fontWeight: 600, fontSize: "0.70rem" },
                sizeSmall: { height: 22 },
            },
        },

        // ── Listas ────────────────────────────────────────────────
        MuiListItem: {
            styleOverrides: { root: { paddingTop: 3, paddingBottom: 3 } },
        },
        MuiListItemText: {
            styleOverrides: {
                root: { margin: 0 },
                primary: { fontSize: "0.8125rem" },
                secondary: { fontSize: "0.72rem" },
            },
        },

        // ── Divider ───────────────────────────────────────────────
        MuiDivider: {
            styleOverrides: { root: { borderColor: "#e2e8f0" } },
        },

        // ── Alert ─────────────────────────────────────────────────
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontSize: "0.8125rem",
                    padding: "6px 14px",
                    alignItems: "center",
                },
            },
        },

        // ── Tooltip ───────────────────────────────────────────────
        MuiTooltip: {
            defaultProps: { arrow: true },
            styleOverrides: {
                tooltip: { fontSize: "0.70rem", backgroundColor: "#1e293b", borderRadius: 5 },
                arrow: { color: "#1e293b" },
            },
        },

        // ── Dialog ────────────────────────────────────────────────
        MuiDialog: {
            styleOverrides: { paper: { borderRadius: 12 } },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: { padding: "14px 18px", fontSize: "0.95rem", fontWeight: 700 },
            },
        },
        MuiDialogContent: {
            styleOverrides: { root: { padding: "12px 18px" } },
        },
        MuiDialogActions: {
            styleOverrides: { root: { padding: "10px 18px" } },
        },

        // ── Timeline ─────────────────────────────────────────────
        MuiTimelineDot: {
            styleOverrides: {
                root: { padding: 4, margin: "3px 0", boxShadow: "none" },
            },
        },
        MuiTimelineContent: {
            styleOverrides: { root: { paddingTop: 2, paddingBottom: 12 } },
        },
        MuiTimelineConnector: {
            styleOverrides: { root: { width: 1, backgroundColor: "#e2e8f0" } },
        },

        // ── DataGrid ──────────────────────────────────────────────
        MuiDataGrid: {
            defaultProps: {
                density: "compact",
                localeText: {
                    MuiTablePagination: {
                        labelRowsPerPage: "Por página:",
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`,
                    },
                    columnMenuLabel: "Menú",
                    columnMenuShowColumns: "Mostrar columnas",
                    columnMenuManageColumns: "Gestionar columnas",
                    columnMenuFilter: "Filtrar",
                    columnMenuHideColumn: "Ocultar columna",
                    columnMenuUnsort: "Sin orden",
                    columnMenuSortAsc: "Orden ascendente",
                    columnMenuSortDesc: "Orden descendente",
                    toolbarColumns: "Columnas",
                    toolbarFilters: "Filtros",
                    toolbarExport: "Exportar",
                    filterPanelAddFilter: "Agregar filtro",
                    filterPanelDeleteIconLabel: "Eliminar",
                    filterPanelOperator: "Operador",
                    filterPanelColumns: "Columnas",
                    filterPanelInputLabel: "Valor",
                    filterPanelInputPlaceholder: "Valor del filtro",
                    noRowsLabel: "No hay filas",
                    noResultsOverlayLabel: "No se encontraron resultados",
                    footerRowSelected: (count) =>
                        `${count} fila${count !== 1 ? "s" : ""} seleccionada${count !== 1 ? "s" : ""}`,
                },
            },
            styleOverrides: {
                // acá van los styleOverrides que ya tenías
            },
        },

        // ── MenuItem ──────────────────────────────────────────────
        MuiMenuItem: {
            styleOverrides: {
                root: { fontSize: "0.8125rem", minHeight: 34 },
            },
        },

        // ── LinearProgress ────────────────────────────────────────
        MuiLinearProgress: {
            styleOverrides: {
                root: { borderRadius: 4, height: 3 },
            },
        },
    },
});

export default auditTheme;